// src/contexts/SettingsContext.js

import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

const defaultSettings = {
  mode: 'default', // 'default' or 'custom'
  defaultResults: 10,
  furnitureResults: 10,
  lightResults: 10,
  accessoriesResults: 10,
  // General Settings
  notifications: true,
  // Data Management
  clearCache: false,
  exportData: false,
  // Appearance Settings
  theme: 'auto', // 'light', 'dark', or 'auto'
  fontSize: 'medium', // 'small', 'medium', 'large'
  layout: 'both', // 'bottom_only', 'center_only', 'both'
};

export const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const storedSettings = localStorage.getItem('appSettings');
    return storedSettings ? JSON.parse(storedSettings) : defaultSettings;
  });

  // Use ref to store media query list to avoid recreating listeners
  const mediaQueryListRef = useRef(null);

  // Memoized helper function to get the actual theme based on user preference
  const getActualTheme = useCallback((themePreference) => {
    if (themePreference === 'auto') {
      // Check if browser supports prefers-color-scheme
      if (window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      // Fallback to light mode for unsupported browsers
      return 'light';
    }
    return themePreference;
  }, []);

  // Memoized function to apply theme to DOM
  const applyTheme = useCallback((actualTheme) => {
    if (actualTheme === 'dark') {
      document.body.className = 'dark-theme';
      document.documentElement.classList.add('dark-theme');
    } else {
      document.body.className = 'light-theme';
      document.documentElement.classList.remove('dark-theme');
      // Remove the injected dark mode style if present
      const darkStyle = document.getElementById('dark-mode-style');
      if (darkStyle) {
        darkStyle.parentNode.removeChild(darkStyle);
      }
    }

    // Update meta theme-color for browser UI
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', actualTheme === 'dark' ? '#2c2c2c' : '#3a8bc5');
    }
  }, []);

  // Memoized theme change handler
  const handleThemeChange = useCallback((e) => {
    const newActualTheme = e.matches ? 'dark' : 'light';
    applyTheme(newActualTheme);
  }, [applyTheme]);

  // Effect for localStorage and general settings (runs on any settings change)
  useEffect(() => {
    try {
      localStorage.setItem('appSettings', JSON.stringify(settings));
    } catch (err) {
      console.error("Error saving settings to localStorage:", err);
    }
  }, [settings]);

  // Effect for theme changes only (runs only when theme changes)
  useEffect(() => {
    const actualTheme = getActualTheme(settings.theme);
    applyTheme(actualTheme);

    // Clean up existing listener
    if (mediaQueryListRef.current) {
      if (mediaQueryListRef.current.removeEventListener) {
        mediaQueryListRef.current.removeEventListener('change', handleThemeChange);
      } else {
        mediaQueryListRef.current.removeListener(handleThemeChange);
      }
      mediaQueryListRef.current = null;
    }

    // Set up new listener only for auto mode
    if (settings.theme === 'auto' && window.matchMedia) {
      mediaQueryListRef.current = window.matchMedia('(prefers-color-scheme: dark)');
      
      // Modern browsers support addEventListener
      if (mediaQueryListRef.current.addEventListener) {
        mediaQueryListRef.current.addEventListener('change', handleThemeChange);
      } else {
        // Fallback for older browsers
        mediaQueryListRef.current.addListener(handleThemeChange);
      }
    }

    // Cleanup function
    return () => {
      if (mediaQueryListRef.current) {
        if (mediaQueryListRef.current.removeEventListener) {
          mediaQueryListRef.current.removeEventListener('change', handleThemeChange);
        } else {
          mediaQueryListRef.current.removeListener(handleThemeChange);
        }
        mediaQueryListRef.current = null;
      }
    };
  }, [settings.theme, getActualTheme, applyTheme, handleThemeChange]);

  // Effect for font size changes only (runs only when fontSize changes)
  useEffect(() => {
    document.body.style.fontSize =
      settings.fontSize === 'small'
        ? '14px'
        : settings.fontSize === 'large'
        ? '18px'
        : '16px';
  }, [settings.fontSize]);

  // Memoized update function
  const updateSettings = useCallback((newSettings) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    settings,
    updateSettings
  }), [settings, updateSettings]);

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};
